import { useEffect, useState } from "react";
import api, { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin, Plus, Trash2, ChevronDown, ChevronRight, Building2, Users, UserPlus, Search, X } from "lucide-react";
import { toast } from "sonner";

export default function LocationMasterPage() {
  const [overview, setOverview] = useState({ districts: [], totals: { districts: 0, branches: 0, dsas: 0 } });
  const [expanded, setExpanded] = useState({});
  const [newDistrict, setNewDistrict] = useState("");
  const [addBranchFor, setAddBranchFor] = useState(null); // district object
  const [assignFor, setAssignFor] = useState(null); // branch object
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/location/overview").then(r => setOverview(r.data)).catch(e => toast.error(errorMessage(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const addDistrict = async () => {
    const name = newDistrict.trim();
    if (!name) return toast.error("Enter district name");
    try { await api.post("/admin/districts", { name }); toast.success("District added"); setNewDistrict(""); load(); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  const delDistrict = async (id) => {
    if (!window.confirm("Delete this district? Its branches will also be removed.")) return;
    try { await api.delete(`/admin/districts/${id}`); toast.success("District deleted"); load(); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  const delBranch = async (id) => {
    if (!window.confirm("Delete this branch? All assigned DSAs will be detached.")) return;
    try { await api.delete(`/admin/branches/${id}`); toast.success("Branch removed"); load(); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div className="space-y-6" data-testid="location-master-page">
      {/* Top Stats */}
      <div className="grid grid-cols-3 gap-4">
        <StatCard label="Total Districts" value={overview.totals.districts} accent="amber" icon={MapPin} testId="stat-districts"/>
        <StatCard label="Total Branches" value={overview.totals.branches} accent="cyan" icon={Building2} testId="stat-branches"/>
        <StatCard label="Total DSAs" value={overview.totals.dsas} accent="emerald" icon={Users} testId="stat-dsas"/>
      </div>

      {/* Add District */}
      <div className="card-surface p-5">
        <div className="text-xs uppercase tracking-widest text-amber-500/80 font-bold mb-3">Add New District</div>
        <div className="flex gap-2 max-w-lg">
          <Input data-testid="loc-new-district" placeholder="District name (e.g., Madurai)" value={newDistrict} onChange={e=>setNewDistrict(e.target.value)} onKeyDown={e=>e.key==="Enter" && addDistrict()} className="bg-slate-950 border-slate-700 h-11"/>
          <Button data-testid="loc-add-district-btn" onClick={addDistrict} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full h-11">
            <Plus className="w-4 h-4 mr-2"/>Add District
          </Button>
        </div>
      </div>

      {/* Districts list */}
      <div className="card-surface p-5">
        <div className="font-heading text-white mb-4 text-lg">Districts &amp; Branches</div>
        {loading ? (
          <div className="text-slate-500 text-center py-10">Loading…</div>
        ) : overview.districts.length === 0 ? (
          <div className="text-slate-500 text-center py-10">No districts yet. Add your first district above.</div>
        ) : (
          <div className="space-y-2">
            {overview.districts.map(d => (
              <DistrictRow
                key={d.id}
                d={d}
                expanded={!!expanded[d.id]}
                onToggle={() => setExpanded(p => ({ ...p, [d.id]: !p[d.id] }))}
                onDelete={() => delDistrict(d.id)}
                onAddBranch={() => setAddBranchFor(d)}
                onAssign={(branch) => setAssignFor({ ...branch, district_name: d.name })}
                onDelBranch={delBranch}
              />
            ))}
          </div>
        )}
      </div>

      <AddBranchDialog district={addBranchFor} onClose={() => setAddBranchFor(null)} onDone={load}/>
      <AssignDsaDialog branch={assignFor} onClose={() => setAssignFor(null)} onDone={load}/>
    </div>
  );
}

function StatCard({ label, value, accent, icon: Icon, testId }) {
  const tones = {
    amber: "from-amber-500/20 to-amber-500/5 border-amber-500/30 text-amber-400",
    cyan:  "from-cyan-500/20 to-cyan-500/5 border-cyan-500/30 text-cyan-400",
    emerald: "from-emerald-500/20 to-emerald-500/5 border-emerald-500/30 text-emerald-400",
  }[accent];
  return (
    <div data-testid={testId} className={`rounded-xl border p-4 bg-gradient-to-br ${tones}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4"/>
        <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">{label}</div>
      </div>
      <div className="font-mono-alt text-3xl font-black text-white">{value}</div>
    </div>
  );
}

function DistrictRow({ d, expanded, onToggle, onDelete, onAddBranch, onAssign, onDelBranch }) {
  return (
    <div className="rounded-xl border border-white/10 bg-slate-950/40" data-testid={`district-row-${d.id}`}>
      <button
        type="button"
        onClick={onToggle}
        data-testid={`district-toggle-${d.id}`}
        className="w-full flex items-center justify-between p-4 hover:bg-white/5"
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="w-4 h-4 text-amber-400"/> : <ChevronRight className="w-4 h-4 text-slate-500"/>}
          <div className="text-white font-heading text-base">{d.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-amber-500/80 font-bold">
            {d.branch_count} {d.branch_count === 1 ? "Branch" : "Branches"} · {d.dsa_count} DSA
          </div>
        </div>
        <button type="button" onClick={(e)=>{e.stopPropagation(); onDelete();}} className="text-red-400 hover:bg-red-500/10 rounded p-1.5" data-testid={`district-del-${d.id}`}>
          <Trash2 className="w-4 h-4"/>
        </button>
      </button>
      {expanded && (
        <div className="border-t border-white/10 p-3 space-y-2">
          {d.branches.length === 0 && (
            <div className="text-xs text-slate-500 text-center py-3">No branches. Add the first one below.</div>
          )}
          {d.branches.map(b => (
            <div key={b.id} data-testid={`branch-row-${b.id}`} className="flex items-center justify-between gap-2 p-3 rounded-lg bg-slate-900/50 border border-white/5 hover:border-amber-500/20">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-white font-semibold">{b.branch_name}</div>
                  <div className="text-[10px] font-mono-alt text-cyan-400/80 uppercase tracking-widest">{b.branch_code}</div>
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[10px] px-2 py-0.5 font-bold uppercase tracking-widest">
                    <Users className="w-3 h-3"/> {b.dsa_count} DSA
                  </span>
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">Assigned DSA: <span className="text-slate-300 font-mono-alt">{b.dsa_count}</span> / 10</div>
              </div>
              <Button size="sm" data-testid={`assign-dsa-${b.id}`} onClick={()=>onAssign(b)} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full h-8 text-xs">
                <UserPlus className="w-3 h-3 mr-1"/> Assign
              </Button>
              <button type="button" onClick={()=>onDelBranch(b.id)} className="text-red-400 hover:bg-red-500/10 rounded p-1.5" data-testid={`branch-del-${b.id}`}>
                <Trash2 className="w-3.5 h-3.5"/>
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={onAddBranch}
            data-testid={`add-branch-btn-${d.id}`}
            className="w-full py-3 rounded-lg border-2 border-dashed border-amber-500/40 text-amber-400 hover:bg-amber-500/5 text-sm font-bold flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4"/> Add Branch
          </button>
        </div>
      )}
    </div>
  );
}

function AddBranchDialog({ district, onClose, onDone }) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [address, setAddress] = useState("");
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [pinning, setPinning] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (district && name) {
      const dp = district.name.replace(/[^A-Z]/gi, "").toUpperCase().slice(0,3);
      const bp = name.replace(/[^A-Z]/gi, "").toUpperCase().slice(0,3);
      setCode(`${dp}-${bp}`);
    } else if (!name) setCode("");
    // eslint-disable-next-line
  }, [name, district]);

  const pin = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported.");
    setPinning(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setLat(+pos.coords.latitude.toFixed(6)); setLng(+pos.coords.longitude.toFixed(6)); setPinning(false); toast.success("Branch GPS pinned"); },
      () => { setPinning(false); toast.error("GPS blocked. Enter lat/lng manually."); },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  const submit = async () => {
    if (!name.trim()) return toast.error("Enter branch name");
    setSaving(true);
    try {
      await api.post("/admin/branches", {
        district_id: district.id, branch_name: name.trim(), branch_code: code.trim(),
        branch_address: address.trim(), branch_lat: lat, branch_lng: lng,
      });
      toast.success("Branch added");
      setName(""); setCode(""); setAddress(""); setLat(null); setLng(null);
      onDone(); onClose();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!district} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md max-h-[92vh] overflow-y-auto" data-testid="add-branch-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-400">
            <Building2 className="w-4 h-4"/> Add Branch
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">District</Label>
            <div className="mt-1 h-11 rounded-md bg-slate-950 border border-slate-700 px-3 flex items-center text-slate-300">{district?.name}</div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Branch Name *</Label>
            <Input data-testid="new-branch-name" autoFocus placeholder="e.g., Jayankondam" value={name} onChange={e=>setName(e.target.value)} className="bg-slate-950 border-slate-700 mt-1 h-11"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Branch Code (Auto)</Label>
            <Input data-testid="new-branch-code" value={code} onChange={e=>setCode(e.target.value.toUpperCase())} className="bg-slate-950 border-slate-700 mt-1 h-11 font-mono-alt uppercase" placeholder="ARI-JAY"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Branch Office Address</Label>
            <Textarea data-testid="new-branch-address" value={address} onChange={e=>setAddress(e.target.value)} className="bg-slate-950 border-slate-700 mt-1" rows={2} placeholder="Full address with pincode"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Branch GPS</Label>
            <Button type="button" onClick={pin} disabled={pinning} data-testid="new-branch-pin" className={`mt-1 w-full h-10 rounded-full font-bold text-xs ${lat!==null ? "bg-emerald-500 text-slate-900" : "bg-amber-500 text-slate-900 hover:bg-amber-400"}`}>
              <MapPin className="w-4 h-4 mr-2"/>{pinning ? "Getting…" : (lat!==null ? `Pinned · ${lat},${lng}` : "📍 Pin Branch on Map")}
            </Button>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Input data-testid="new-branch-lat" value={lat ?? ""} onChange={e=>setLat(e.target.value===""?null:parseFloat(e.target.value))} placeholder="Latitude" className="bg-slate-950 border-slate-700 h-9 font-mono-alt text-xs"/>
              <Input data-testid="new-branch-lng" value={lng ?? ""} onChange={e=>setLng(e.target.value===""?null:parseFloat(e.target.value))} placeholder="Longitude" className="bg-slate-950 border-slate-700 h-9 font-mono-alt text-xs"/>
            </div>
            {lat!==null && lng!==null && (
              <iframe title="branch preview" className="w-full h-36 rounded-lg mt-2 border border-emerald-500/40" src={`https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`} loading="lazy"/>
            )}
          </div>
          <Button data-testid="save-branch-btn" disabled={saving} onClick={submit} className="w-full h-11 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">
            {saving ? "Saving…" : "Save Branch"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AssignDsaDialog({ branch, onClose, onDone }) {
  const [pool, setPool] = useState({ same_district: [], other: [] });
  const [existing, setExisting] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!branch) return;
    setLoading(true);
    Promise.all([
      api.get(`/admin/branches/${branch.id}/assignable`).then(r => setPool(r.data)),
      api.get(`/admin/branches/${branch.id}/dsas`).then(r => setExisting(r.data)),
    ]).finally(() => setLoading(false));
  }, [branch]);

  const assign = async (uid) => {
    try {
      await api.post(`/admin/branches/${branch.id}/assign-dsa`, { user_id: uid });
      toast.success("Assigned");
      const [a, b] = await Promise.all([
        api.get(`/admin/branches/${branch.id}/assignable`),
        api.get(`/admin/branches/${branch.id}/dsas`),
      ]);
      setPool(a.data); setExisting(b.data);
      onDone();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const unassign = async (uid) => {
    try {
      await api.post(`/admin/branches/${branch.id}/unassign-dsa`, { user_id: uid });
      toast.success("Removed");
      const [a, b] = await Promise.all([
        api.get(`/admin/branches/${branch.id}/assignable`),
        api.get(`/admin/branches/${branch.id}/dsas`),
      ]);
      setPool(a.data); setExisting(b.data);
      onDone();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const filter = (arr) => q ? arr.filter(u => (u.name+u.phone).toLowerCase().includes(q.toLowerCase())) : arr;

  return (
    <Dialog open={!!branch} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="assign-dsa-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-400">
            <UserPlus className="w-4 h-4"/> Assign DSA · {branch?.branch_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {existing.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-1">Currently Assigned ({existing.length})</div>
              <div className="space-y-1">
                {existing.map(u => (
                  <div key={u.id} className="flex items-center justify-between gap-2 p-2 rounded bg-emerald-500/10 border border-emerald-500/30" data-testid={`assigned-user-${u.id}`}>
                    <div className="min-w-0">
                      <div className="text-white text-sm truncate">{u.name}</div>
                      <div className="text-[10px] text-slate-400 font-mono-alt">{u.phone} · {u.role === "partner" ? (u.partner_id || "PENDING") : "Office Staff"}</div>
                    </div>
                    <button data-testid={`unassign-${u.id}`} onClick={()=>unassign(u.id)} className="text-red-400 hover:bg-red-500/10 rounded p-1.5"><X className="w-4 h-4"/></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"/>
            <Input data-testid="assign-search" placeholder="Search unassigned partners / office staff…" value={q} onChange={e=>setQ(e.target.value)} className="bg-slate-950 border-slate-700 pl-9"/>
          </div>

          {loading ? <div className="text-slate-500 text-center py-6">Loading pool…</div> : (
            <>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-bold mb-1">Same District — {branch?.district_name}</div>
                {filter(pool.same_district).length === 0 && <div className="text-xs text-slate-500 py-2">No unassigned users in this district.</div>}
                <div className="space-y-1">
                  {filter(pool.same_district).map(u => <PoolRow key={u.id} u={u} onAssign={assign}/>)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Other Districts</div>
                <div className="space-y-1">
                  {filter(pool.other).map(u => <PoolRow key={u.id} u={u} onAssign={assign}/>)}
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PoolRow({ u, onAssign }) {
  return (
    <div className="flex items-center justify-between gap-2 p-2 rounded bg-slate-950/60 border border-white/10 hover:border-amber-500/30" data-testid={`pool-user-${u.id}`}>
      <div className="min-w-0">
        <div className="text-white text-sm truncate">{u.name}</div>
        <div className="text-[10px] text-slate-400 font-mono-alt">{u.phone} · {u.role === "partner" ? (u.partner_id || "PENDING VERIFY") : "Office Staff"} · {u.district || "—"}</div>
      </div>
      <Button size="sm" data-testid={`assign-${u.id}`} onClick={()=>onAssign(u.id)} className="bg-amber-500 hover:bg-amber-400 text-slate-900 h-7 text-xs rounded-full">Assign</Button>
    </div>
  );
}
